/**
 * Admin · Orders cockpit.
 *
 * Filterable, paginated table of every order on the platform with
 * inline shortcuts to mark-shipped or open the full order detail.
 * Most of the heavy lifting (auth, real ship action, refund) lives on
 * the existing OrderDetail page — this page is a focused triage view
 * so support can find an order in 2 seconds.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ArrowsClockwiseIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  PackageIcon,
  TruckIcon,
  ArrowRightIcon,
  LightningIcon,
} from "@phosphor-icons/react";

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "awaiting_ship", label: "Paid · awaiting ship" },
  { value: "stale_shipped", label: "Stale shipped (>7d)" },
  { value: "pending", label: "Pending" },
  { value: "shipped", label: "Shipped" },
  { value: "delivered", label: "Delivered" },
  { value: "refunded", label: "Refunded" },
];
const DAYS_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "1", label: "Last 24h" },
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
];
const PAGE_SIZE = 50;

function StatusPill({ value }) {
  const styles = {
    pending: "bg-amber-100 text-amber-800 border-amber-300",
    shipped: "bg-sky-100 text-sky-800 border-sky-300",
    delivered: "bg-emerald-100 text-emerald-800 border-emerald-300",
    refunded: "bg-rose-100 text-rose-800 border-rose-300",
  };
  const cls = styles[value] || "bg-zinc-100 text-zinc-700 border-zinc-300";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`}
    >
      {value || "unknown"}
    </span>
  );
}

export default function AdminOrders() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("all");
  const [days, setDays] = useState("all");
  const [q, setQ] = useState("");
  const [searched, setSearched] = useState("");
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  // Bulk-ship selection — set of order ids the admin has ticked. We
  // automatically prune ids that fall out of view (page change / search)
  // so the bulk count never lies about what will actually be acted on.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Gate-keep: non-admins get bounced. We wait for the auth context to
  // finish hydrating to avoid a flash redirect on refresh.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login");
    } else if (user.role !== "admin") {
      toast.error("Admins only");
      navigate("/");
    }
  }, [user, authLoading, navigate]);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset };
      if (status !== "all") params.status = status;
      if (days !== "all") params.days = Number(days);
      if (searched.trim()) params.q = searched.trim();
      const { data } = await api.get("/admin/orders", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
    } catch (e) {
      toast.error(apiError(e) || "Could not load orders");
    } finally {
      setLoading(false);
    }
  }, [status, days, searched, offset]);

  useEffect(() => {
    if (user?.role === "admin") fetchPage();
  }, [user, fetchPage]);

  // Surgical row mutation after a Quick-ship succeeds — avoids a full
  // refetch (and the resulting visual jump) when an admin processes
  // multiple pending orders in a batch.
  const patchOrder = (orderId, patch) => {
    setItems((rows) =>
      rows.map((r) => (r.id === orderId ? { ...r, ...patch } : r))
    );
    // Once a row is no longer Pending, drop it from the bulk-ship
    // selection so the count + button label stay truthful.
    if (patch.shipping_status && patch.shipping_status !== "pending") {
      setSelected((prev) => {
        if (!prev.has(orderId)) return prev;
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };

  // Drop a stale row when the backend reports it as gone. Keeps the
  // cockpit honest when an admin tries to ship/refund something a
  // teammate already actioned in another tab.
  const removeOrder = (orderId) => {
    setItems((rows) => rows.filter((r) => r.id !== orderId));
    setSelected((prev) => {
      if (!prev.has(orderId)) return prev;
      const next = new Set(prev);
      next.delete(orderId);
      return next;
    });
  };

  // Eligible-for-bulk = pending physical orders currently on screen.
  const bulkEligibleIds = items
    .filter((o) => !o.is_digital && o.shipping_status === "pending")
    .map((o) => o.id);
  const selectedEligible = bulkEligibleIds.filter((id) => selected.has(id));
  const allEligibleSelected =
    bulkEligibleIds.length > 0 && selectedEligible.length === bulkEligibleIds.length;

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAllEligible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allEligibleSelected) {
        bulkEligibleIds.forEach((id) => next.delete(id));
      } else {
        bulkEligibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const runBulkShip = async ({ carrier, tracking_number }) => {
    const order_ids = Array.from(selected).filter((id) => bulkEligibleIds.includes(id));
    if (!order_ids.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/orders/bulk-ship", {
        order_ids,
        carrier,
        tracking_number: tracking_number?.trim() || null,
      });
      // Apply the per-id results: flip the successful rows, surface
      // partial failures via toast.
      const okIds = (data.results || [])
        .filter((r) => r.ok)
        .map((r) => r.order_id);
      okIds.forEach((id) =>
        patchOrder(id, {
          shipping_status: "shipped",
          carrier,
          tracking_number: tracking_number?.trim() || null,
        })
      );
      // Stale-row cleanup — if any of the selected ids returned 404
      // they no longer exist (or were already shipped/refunded), so
      // drop them from the table so the admin's view matches reality.
      const missingIds = (data.results || [])
        .filter((r) => !r.ok && r.status === 404)
        .map((r) => r.order_id);
      missingIds.forEach((id) => removeOrder(id));
      // Reconcile mismatched rows. If the backend rejects with
      // "Order already shipped" or "This is a digital product" the row
      // in our table is stale or wrong — patch its visible status so
      // the admin doesn't re-attempt the same selection again.
      (data.results || []).forEach((r) => {
        if (r.ok) return;
        const err = String(r.error || "").toLowerCase();
        if (err.includes("already shipped")) {
          patchOrder(r.order_id, { shipping_status: "shipped" });
        } else if (err.includes("already delivered")) {
          patchOrder(r.order_id, { shipping_status: "delivered" });
        } else if (err.includes("digital product")) {
          patchOrder(r.order_id, { is_digital: true });
        }
      });
      const failed = (data.results || []).filter((r) => !r.ok);
      // Build a short, human-readable list of failure reasons so the
      // admin can immediately see WHY each order was skipped instead of
      // having to open them one by one.
      const reasonSummary = failed.length
        ? failed.slice(0, 3).map((f) => {
            const shortId = (f.order_id || "").slice(-6);
            const reason = (f.error || `HTTP ${f.status}`).replace(/\.$/, "");
            return `· ${shortId}: ${reason}`;
          }).join("\n") + (failed.length > 3 ? `\n· …and ${failed.length - 3} more` : "")
        : "";

      if (failed.length && data.shipped === 0) {
        toast.message(
          `0 of ${data.total} shipped — refreshing the list.`,
          { description: reasonSummary }
        );
        fetchPage();
      } else if (failed.length) {
        toast.warning(
          `${data.shipped}/${data.total} shipped. ${failed.length} skipped.`,
          { description: reasonSummary }
        );
      } else {
        toast.success(`Shipped ${data.shipped} order${data.shipped === 1 ? "" : "s"}`);
      }
      clearSelection();
    } catch (e) {
      toast.error(apiError(e) || "Bulk ship failed");
    } finally {
      setBulkBusy(false);
    }
  };

  // Bulk refund — admins can refund any selected paid orders. We only
  // attempt orders that aren't already refunded; the backend dedupes
  // and returns per-id results so partial failures are visible.
  const [refundBusy, setRefundBusy] = useState(false);
  const bulkRefundEligibleIds = items
    .filter((o) => o.status !== "refunded" && o.shipping_status !== "refunded")
    .map((o) => o.id);
  const selectedRefundable = bulkRefundEligibleIds.filter((id) => selected.has(id));
  const selectedRefundAmount = items
    .filter((o) => selectedRefundable.includes(o.id))
    .reduce((acc, o) => acc + Number(o.amount || 0), 0);

  const runBulkRefund = async ({ reason }) => {
    if (!selectedRefundable.length) return;
    setRefundBusy(true);
    try {
      const { data } = await api.post("/admin/orders/bulk-refund", {
        order_ids: selectedRefundable,
        reason,
      });
      const okIds = (data.results || [])
        .filter((r) => r.ok && r.status !== "already_refunded")
        .map((r) => r.order_id);
      okIds.forEach((id) =>
        patchOrder(id, {
          status: "refunded",
          shipping_status: "refunded",
          payment_status: "refunded",
        })
      );
      const missingIds = (data.results || [])
        .filter((r) => !r.ok && r.status === 404)
        .map((r) => r.order_id);
      missingIds.forEach((id) => removeOrder(id));
      const failed = (data.results || []).filter((r) => !r.ok);
      const reasonSummary = failed.length
        ? failed.slice(0, 3).map((f) => {
            const shortId = (f.order_id || "").slice(-6);
            const reason = (f.error || `HTTP ${f.status}`).replace(/\.$/, "");
            return `· ${shortId}: ${reason}`;
          }).join("\n") + (failed.length > 3 ? `\n· …and ${failed.length - 3} more` : "")
        : "";

      if (failed.length && data.refunded === 0 && missingIds.length === failed.length) {
        toast.message(
          `0 of ${data.total} refunded — refreshing the list.`,
          { description: reasonSummary }
        );
        fetchPage();
      } else if (failed.length) {
        toast.warning(
          `Refunded $${data.total_amount.toFixed(2)} across ${data.refunded} order${data.refunded === 1 ? "" : "s"}. ${failed.length} skipped.`,
          { description: reasonSummary }
        );
      } else {
        toast.success(
          `Refunded $${data.total_amount.toFixed(2)} across ${data.refunded} order${data.refunded === 1 ? "" : "s"}`
        );
      }
      clearSelection();
    } catch (e) {
      toast.error(apiError(e) || "Bulk refund failed");
    } finally {
      setRefundBusy(false);
    }
  };

  // Bulk deliver — force-mark "shipped, awaiting buyer confirm"
  // orders as delivered (releases the seller payouts that are stuck
  // on hold). Eligible = currently shipped & not yet delivered.
  // Stale-shipped filter scopes the cockpit to >7-day-old shipped
  // orders so admins can sweep the whole stuck pool in one click.
  const [deliverBusy, setDeliverBusy] = useState(false);
  const bulkDeliverEligibleIds = items
    .filter(
      (o) => o.shipping_status === "shipped" && o.status !== "refunded"
    )
    .map((o) => o.id);
  const selectedDeliverable = bulkDeliverEligibleIds.filter((id) => selected.has(id));

  const runBulkDeliver = async () => {
    if (!selectedDeliverable.length) return;
    setDeliverBusy(true);
    try {
      const { data } = await api.post("/admin/orders/bulk-deliver", {
        order_ids: selectedDeliverable,
      });
      const okIds = (data.results || [])
        .filter((r) => r.ok)
        .map((r) => r.order_id);
      const now = new Date().toISOString();
      okIds.forEach((id) =>
        patchOrder(id, {
          status: "delivered",
          shipping_status: "delivered",
          delivered_at: now,
        })
      );
      const missingIds = (data.results || [])
        .filter((r) => !r.ok && r.status === 404)
        .map((r) => r.order_id);
      missingIds.forEach((id) => removeOrder(id));
      // Reconcile mismatched rows so the admin's view matches reality.
      (data.results || []).forEach((r) => {
        if (r.ok) return;
        const err = String(r.error || "").toLowerCase();
        if (err.includes("already")) {
          patchOrder(r.order_id, { shipping_status: "delivered" });
        }
      });
      const failed = (data.results || []).filter((r) => !r.ok);
      const reasonSummary = failed.length
        ? failed.slice(0, 3).map((f) => {
            const shortId = (f.order_id || "").slice(-6);
            const reason = (f.error || `HTTP ${f.status}`).replace(/\.$/, "");
            return `· ${shortId}: ${reason}`;
          }).join("\n") + (failed.length > 3 ? `\n· …and ${failed.length - 3} more` : "")
        : "";

      if (failed.length && data.delivered === 0 && missingIds.length === failed.length) {
        toast.message(
          `0 of ${data.total} delivered — refreshing the list.`,
          { description: reasonSummary }
        );
        fetchPage();
      } else if (failed.length) {
        toast.warning(
          `Delivered ${data.delivered} of ${data.total} · ${failed.length} skipped`,
          { description: reasonSummary }
        );
      } else {
        toast.success(
          `Marked ${data.delivered} order${data.delivered === 1 ? "" : "s"} as delivered`,
          { description: "Seller payouts released." }
        );
      }
      clearSelection();
    } catch (e) {
      toast.error(apiError(e) || "Bulk deliver failed");
    } finally {
      setDeliverBusy(false);
    }
  };

  const onSearch = (e) => {
    e?.preventDefault?.();
    setOffset(0);
    setSearched(q);
  };

  // Whenever a filter changes we snap back to the first page so the
  // result count makes sense to the admin reading it.
  const onFilterChange = (setter) => (value) => {
    setOffset(0);
    setter(value);
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="admin-orders-page">
      <Navbar />
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-10">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="overline text-zinc-500">ADMIN · OPERATIONS</p>
            <h1 className="font-display font-black text-4xl tracking-tighter">
              Orders cockpit
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Search any order on the platform. Tap "Open" to mark shipped, view tracking, or refund.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchPage()}
            disabled={loading}
            data-testid="admin-orders-refresh"
            className="gap-2"
          >
            <ArrowsClockwiseIcon size={14} /> Refresh
          </Button>
        </div>

        <form
          onSubmit={onSearch}
          className="border border-zinc-200 bg-white rounded-2xl p-4 mb-6 grid sm:grid-cols-[1fr_auto_auto_auto] gap-3"
        >
          <div className="relative">
            <MagnifyingGlassIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search order id, buyer email, seller, tracking…"
              className="pl-9"
              data-testid="admin-orders-search-input"
            />
          </div>
          <Select value={status} onValueChange={onFilterChange(setStatus)}>
            <SelectTrigger className="w-44" data-testid="admin-orders-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={days} onValueChange={onFilterChange(setDays)}>
            <SelectTrigger className="w-40" data-testid="admin-orders-days-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DAYS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="submit"
            className="bg-[#1D78AB] hover:bg-[#165d85] text-white"
            data-testid="admin-orders-search-btn"
          >
            Search
          </Button>
        </form>

        <div className="border border-zinc-200 bg-white rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between bg-zinc-50 gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              {bulkEligibleIds.length > 0 && (
                <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="admin-orders-select-all-label">
                  <Checkbox
                    checked={allEligibleSelected}
                    onCheckedChange={toggleAllEligible}
                    data-testid="admin-orders-select-all"
                    aria-label="Select all pending orders on this page"
                  />
                  <span className="text-xs uppercase tracking-wider font-bold text-zinc-500">
                    Select pending ({bulkEligibleIds.length})
                  </span>
                </label>
              )}
              <p className="text-xs uppercase tracking-wider font-bold text-zinc-500">
                {loading ? "Loading…" : `${total} match${total === 1 ? "" : "es"}`}
              </p>
            </div>
            {searched && (
              <button
                type="button"
                onClick={() => {
                  setQ("");
                  setSearched("");
                  setOffset(0);
                }}
                className="text-xs font-semibold text-[#1D78AB] hover:underline"
                data-testid="admin-orders-clear"
              >
                Clear search
              </button>
            )}
          </div>

          {items.length === 0 && !loading ? (
            <div className="p-12 text-center">
              <PackageIcon size={36} className="text-zinc-300 mx-auto mb-2" />
              <p className="font-display font-bold text-lg">No orders match those filters</p>
              <p className="text-sm text-zinc-500 mt-1">Try a different status, time range, or keyword.</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-200">
              {items.map((o) => (
                <OrderRow
                  key={o.id}
                  order={o}
                  onShipped={patchOrder}
                  onMissing={removeOrder}
                  selected={selected.has(o.id)}
                  onToggle={toggleOne}
                />
              ))}
            </div>
          )}

          {(offset > 0 || hasMore) && (
            <div className="border-t border-zinc-200 px-4 py-3 flex items-center justify-between bg-zinc-50">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0 || loading}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                data-testid="admin-orders-prev"
              >
                ← Prev
              </Button>
              <p className="text-xs text-zinc-500 font-mono">
                {offset + 1} – {Math.min(offset + items.length, total)} of {total}
              </p>
              <Button
                variant="outline"
                size="sm"
                disabled={!hasMore || loading}
                onClick={() => setOffset(offset + PAGE_SIZE)}
                data-testid="admin-orders-next"
              >
                Next →
              </Button>
            </div>
          )}
        </div>
      </main>
      <BulkActionBar
        shipCount={selectedEligible.length}
        refundCount={selectedRefundable.length}
        refundTotal={selectedRefundAmount}
        deliverCount={selectedDeliverable.length}
        shipBusy={bulkBusy}
        refundBusy={refundBusy}
        deliverBusy={deliverBusy}
        onCancel={clearSelection}
        onShip={runBulkShip}
        onRefund={runBulkRefund}
        onDeliver={runBulkDeliver}
      />
      <Footer />
    </div>
  );
}

function OrderRow({ order, onShipped, onMissing, selected, onToggle }) {
  const amount = Number(order.amount || 0).toFixed(2);
  const created = order.created_at
    ? new Date(order.created_at).toLocaleString()
    : "—";
  const Icon = order.shipping_status === "shipped" ? TruckIcon : PackageIcon;
  // Only pending physical orders are eligible for Quick-ship; digital
  // orders auto-fulfill at payment and shipped/delivered ones are
  // already past this step.
  const canQuickShip =
    !order.is_digital && order.shipping_status === "pending";
  // Selectable for bulk actions whenever the order isn't already
  // refunded/cancelled. Bulk-ship will still only fire on the pending
  // subset; bulk-refund applies to anything non-refunded.
  const canSelect = !["refunded", "cancelled"].includes(order.status);
  return (
    <div
      className={`px-4 py-3 hover:bg-zinc-50 transition-colors grid grid-cols-[auto_auto_1fr_auto] sm:grid-cols-[auto_auto_1fr_auto_auto] gap-3 items-center ${selected ? "bg-emerald-50/40" : ""}`}
      data-testid={`admin-order-row-${order.id}`}
    >
      {canSelect ? (
        <Checkbox
          checked={!!selected}
          onCheckedChange={() => onToggle?.(order.id)}
          data-testid={`admin-order-select-${order.id}`}
          aria-label={`Select order ${order.id}`}
        />
      ) : (
        <span className="w-4" aria-hidden />
      )}
      <Icon size={22} className="text-zinc-400 hidden sm:block" />
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-display font-bold text-sm truncate">
            {order.listing_title || "—"}
          </p>
          <StatusPill value={order.shipping_status} />
          {order.is_digital && (
            <span className="inline-flex items-center rounded-full bg-[#1D78AB]/10 text-[#1D78AB] border border-[#1D78AB]/30 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
              Digital
            </span>
          )}
        </div>
        <p className="text-[11px] font-mono text-zinc-500 mt-1 truncate">
          {order.id} · {created}
        </p>
        <p className="text-[11px] text-zinc-500 mt-0.5 truncate">
          Buyer{" "}
          <span className="text-zinc-700">{order.buyer_email || order.buyer_username || order.buyer_id}</span>
          {" · "}
          Seller{" "}
          <span className="text-zinc-700">{order.seller_email || order.seller_username || order.seller_id}</span>
          {order.tracking_number && (
            <>
              {" · "}
              Tracking <span className="text-zinc-700">{order.tracking_number}</span>
            </>
          )}
        </p>
      </div>
      <p className="font-mono font-bold text-[#1D78AB] text-right">${amount}</p>
      <div className="flex items-center gap-2 flex-shrink-0">
        {canQuickShip && (
          <QuickShipPopover order={order} onShipped={onShipped} onMissing={onMissing} />
        )}
        <Link
          to={`/orders/${order.id}`}
          className="inline-flex items-center gap-1 rounded-full bg-[#1D78AB] hover:bg-[#165d85] text-white text-xs font-bold px-3 py-2"
          data-testid={`admin-order-open-${order.id}`}
        >
          Open <ArrowRightIcon size={12} weight="bold" />
        </Link>
      </div>
    </div>
  );
}

function BulkActionBar({
  shipCount,
  refundCount,
  refundTotal,
  deliverCount,
  shipBusy,
  refundBusy,
  deliverBusy,
  onCancel,
  onShip,
  onRefund,
  onDeliver,
}) {
  const [shipOpen, setShipOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("usps");
  const [refundReason, setRefundReason] = useState("");

  const totalSelected = Math.max(shipCount, refundCount, deliverCount || 0);
  if (totalSelected <= 0) return null;

  const submitShip = (e) => {
    e?.preventDefault?.();
    onShip?.({ carrier, tracking_number: tracking });
    setShipOpen(false);
    setTracking("");
  };
  const confirmRefund = () => {
    if (refundReason.trim().length < 3) {
      toast.error("Reason is required (min 3 chars)");
      return;
    }
    onRefund?.({ reason: refundReason.trim() });
    setRefundOpen(false);
    setRefundReason("");
  };
  const confirmDeliver = () => {
    if (!window.confirm(
      `Force-mark ${deliverCount} order${deliverCount === 1 ? "" : "s"} as delivered? ` +
      `This releases the seller payouts immediately.`
    )) return;
    onDeliver?.();
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 border-t border-zinc-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80"
      data-testid="admin-orders-bulk-bar"
      role="region"
      aria-label="Bulk action selection"
    >
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center rounded-full bg-emerald-500 text-white font-bold text-sm h-7 min-w-7 px-2">
            {totalSelected}
          </span>
          <p className="font-display font-bold text-sm">
            order{totalSelected === 1 ? "" : "s"} selected
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={shipBusy || refundBusy}
            data-testid="admin-orders-bulk-cancel"
          >
            Clear
          </Button>

          {/* Bulk deliver — only shown when at least one currently-shipped
              order is selected. Releases held seller payouts. */}
          {deliverCount > 0 && (
            <Button
              size="sm"
              disabled={deliverBusy || shipBusy || refundBusy}
              onClick={confirmDeliver}
              className="bg-amber-500 hover:bg-amber-600 text-white gap-2"
              data-testid="admin-orders-bulk-deliver-trigger"
            >
              <CheckCircleIcon size={14} weight="bold" />
              {deliverBusy
                ? "Delivering…"
                : `Mark ${deliverCount} delivered`}
            </Button>
          )}

          {/* Bulk refund */}
          {refundCount > 0 && (
            <Button
              size="sm"
              variant="outline"
              disabled={refundBusy || shipBusy}
              onClick={() => setRefundOpen(true)}
              className="gap-2 border-rose-400 text-rose-700 hover:bg-rose-50"
              data-testid="admin-orders-bulk-refund-trigger"
            >
              {refundBusy ? "Refunding…" : `Refund ${refundCount} ($${refundTotal.toFixed(2)})`}
            </Button>
          )}

          {/* Bulk ship */}
          {shipCount > 0 && (
            <Popover open={shipOpen} onOpenChange={setShipOpen}>
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  disabled={shipBusy || refundBusy}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                  data-testid="admin-orders-bulk-ship-trigger"
                >
                  <LightningIcon size={14} weight="bold" />
                  {shipBusy ? "Shipping…" : `Bulk ship ${shipCount}`}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                side="top"
                className="w-80"
                data-testid="admin-orders-bulk-ship-popover"
              >
                <form onSubmit={submitShip} className="space-y-3">
                  <div>
                    <p className="font-display font-bold text-sm">Bulk ship {shipCount} order{shipCount === 1 ? "" : "s"}</p>
                    <p className="text-[11px] text-zinc-500">
                      Same carrier {tracking ? "& tracking" : "(no tracking)"} applied to all.
                    </p>
                  </div>
                  <div>
                    <Label className="overline">
                      Tracking <span className="text-zinc-400 normal-case font-medium">(optional · same for all)</span>
                    </Label>
                    <Input
                      value={tracking}
                      onChange={(e) => setTracking(e.target.value)}
                      placeholder="Leave blank to override"
                      className="mt-1"
                      data-testid="admin-orders-bulk-tracking"
                    />
                  </div>
                  <div>
                    <Label className="overline">Carrier</Label>
                    <select
                      value={carrier}
                      onChange={(e) => setCarrier(e.target.value)}
                      className="w-full mt-1 h-9 border border-zinc-300 bg-white px-2 text-sm rounded"
                      data-testid="admin-orders-bulk-carrier"
                    >
                      {CARRIER_CHOICES.map((c) => (
                        <option key={c.slug} value={c.slug}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center justify-end gap-2 pt-1">
                    <Button type="button" variant="ghost" size="sm" onClick={() => setShipOpen(false)} disabled={shipBusy}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      size="sm"
                      disabled={shipBusy}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="admin-orders-bulk-submit"
                    >
                      {shipBusy ? "Shipping…" : "Mark shipped"}
                    </Button>
                  </div>
                </form>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      {/* Refund confirmation modal — separate from ship popover because
          refunds are dollars-sensitive and deserve a more deliberate UX */}
      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent className="sm:max-w-md" data-testid="admin-orders-bulk-refund-dialog">
          <DialogHeader>
            <DialogTitle>Refund {refundCount} order{refundCount === 1 ? "" : "s"}?</DialogTitle>
            <DialogDescription>
              This refunds <span className="font-bold">${refundTotal.toFixed(2)}</span> through the original payment method (Stripe or PayPal). The buyer is notified instantly. Reason will be saved to the audit log.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="overline">Reason (required)</Label>
            <Textarea
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              maxLength={500}
              rows={4}
              placeholder="e.g., Seller cancelled, item not as described, duplicate charge…"
              data-testid="admin-orders-bulk-refund-reason"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setRefundOpen(false)}
              disabled={refundBusy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmRefund}
              disabled={refundBusy || refundReason.trim().length < 3}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="admin-orders-bulk-refund-confirm"
            >
              {refundBusy ? "Refunding…" : `Refund $${refundTotal.toFixed(2)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const CARRIER_CHOICES = [
  { slug: "usps", name: "USPS" },
  { slug: "ups", name: "UPS" },
  { slug: "fedex", name: "FedEx" },
  { slug: "dhl", name: "DHL" },
  { slug: "ontrac", name: "OnTrac" },
];

function QuickShipPopover({ order, onShipped, onMissing }) {
  const [open, setOpen] = useState(false);
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("usps");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/ship`, {
        // Admin override allows omitting tracking entirely.
        tracking_number: tracking.trim() || null,
        carrier,
      });
      toast.success("Marked shipped");
      onShipped?.(order.id, {
        shipping_status: "shipped",
        carrier: data.carrier,
        carrier_slug: data.carrier_slug,
        tracking_number: data.tracking_number,
      });
      setOpen(false);
      setTracking("");
    } catch (err) {
      // The most common failure here is the row being stale — the
      // order was already shipped/refunded/deleted by someone else
      // since the page loaded. Drop the row + nudge the admin to
      // refresh so the cockpit reflects DB truth instead of leaving
      // a confusing red "Not found" toast.
      if (err?.response?.status === 404) {
        toast.message("That order is no longer pending — refreshed the list.");
        onMissing?.(order.id);
        setOpen(false);
        return;
      }
      toast.error(apiError(err) || "Could not mark shipped");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-full border border-emerald-500 text-emerald-700 hover:bg-emerald-50 text-xs font-bold px-3 py-2"
          data-testid={`admin-quick-ship-${order.id}`}
        >
          <LightningIcon size={12} weight="bold" /> Quick ship
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80" data-testid={`admin-quick-ship-popover-${order.id}`}>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <p className="font-display font-bold text-sm">Quick ship</p>
            <p className="text-[11px] text-zinc-500 truncate">{order.id}</p>
          </div>
          <div>
            <Label className="overline">
              Tracking <span className="text-zinc-400 normal-case font-medium">(optional · admin override)</span>
            </Label>
            <Input
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
              placeholder="Leave blank to override"
              className="mt-1"
              data-testid={`quick-ship-tracking-${order.id}`}
            />
          </div>
          <div>
            <Label className="overline">Carrier</Label>
            <select
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              className="w-full mt-1 h-9 border border-zinc-300 bg-white px-2 text-sm rounded"
              data-testid={`quick-ship-carrier-${order.id}`}
            >
              {CARRIER_CHOICES.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={busy}
              className="bg-[#1D78AB] hover:bg-[#165d85] text-white"
              data-testid={`quick-ship-submit-${order.id}`}
            >
              {busy ? "Shipping…" : "Mark shipped"}
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
